/**
 * Teaching levels are a banded number space, shared with the resources collection (zl-api ADR 0003):
 * `0` Kindergarten, `1`-`12` the numbered grades, `13`-`19` reserved for numbered grades no school
 * system we serve uses yet, `20` Post-secondary, `255` Other.
 *
 * The label map is the picker's order too, which is why Post-secondary sits between Grade 12 and
 * Other rather than after it — Other stays the trailing catch-all.
 *
 * zl-api holds the same table in `@zl/utils/teachingLevels`, where it drives the mongoose enum and
 * the resource labels. The two repos are separate deployables, so the tables agree by review rather
 * than by import: change one and change the other in the same PR.
 */
export const NUMBER_TO_LEVEL: Record<number, string> = {
  0: 'Kindergarten',
  1: 'Grade 1',
  2: 'Grade 2',
  3: 'Grade 3',
  4: 'Grade 4',
  5: 'Grade 5',
  6: 'Grade 6',
  7: 'Grade 7',
  8: 'Grade 8',
  9: 'Grade 9',
  10: 'Grade 10',
  11: 'Grade 11',
  12: 'Grade 12',
  20: 'Post-secondary',
  255: 'Other',
};

export const LEVEL_TO_NUMBER: Record<string, number> = Object.fromEntries(
  Object.entries(NUMBER_TO_LEVEL).map(([number, level]) => [level, parseInt(number, 10)]),
);

export const LEVEL_OPTIONS = Object.values(NUMBER_TO_LEVEL);

/**
 * The kind of setting somebody teaches in, as against the age of the pupils they teach — which is
 * what the levels above answer. Early Childhood here names an institution; Kindergarten there names
 * a stage, and the two are answered independently.
 */
export const TEACHING_TYPE_TO_LABEL = {
  earlyChildhood: 'Early childhood',
  homeschool: 'Homeschool',
  public: 'Public',
  private: 'Private',
  charter: 'Charter',
  microSchool: 'Micro-school',
  other: 'Other',
} as const;

export type TeachingType = keyof typeof TEACHING_TYPE_TO_LABEL;

export const LABEL_TO_TEACHING_TYPE = Object.fromEntries(
  Object.entries(TEACHING_TYPE_TO_LABEL).map(([type, label]) => [label, type]),
) as Record<string, TeachingType>;

export const TEACHING_TYPE_OPTIONS = Object.values(TEACHING_TYPE_TO_LABEL);
